import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useApexTheme, mergeThemeOptions } from '../utils/apexTheme';

// Helper to create histogram bins
const createHistogramBins = (values, numBins = 15) => {
  if (!values || values.length === 0) return [];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const binWidth = (max - min) / numBins;

  const bins = [];
  for (let i = 0; i < numBins; i++) {
    bins.push({
      min: min + i * binWidth,
      max: min + (i + 1) * binWidth,
      count: 0
    });
  }

  values.forEach(val => {
    const binIndex = Math.min(Math.floor((val - min) / binWidth), numBins - 1);
    if (binIndex >= 0 && binIndex < bins.length) {
      bins[binIndex].count++;
    }
  });

  return bins;
};

const ReturnsHistogram = ({ returnsSeries, height = 250 }) => {
  const { baseThemeOptions, colors, isDark } = useApexTheme();

  const { options, series, bins } = useMemo(() => {
    if (!returnsSeries || returnsSeries.length === 0) return { options: null, series: [], bins: [] };

    // Convert returns to percentage
    const returns = returnsSeries.map(r => r.return * 100);

    // Calculate statistics
    const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / returns.length;
    const stdDev = Math.sqrt(variance);

    // Create histogram bins
    const histogramBins = createHistogramBins(returns, 25);

    const data = histogramBins.map(bin => ({
      x: ((bin.min + bin.max) / 2).toFixed(2) + '%',
      y: bin.count,
      low: bin.min,
      high: bin.max
    }));

    const componentOptions = {
      chart: {
        type: 'bar',
        height,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 2,
          columnWidth: '90%'
        }
      },
      dataLabels: {
        enabled: false
      },
      colors: [colors.primary || '#2196f3'],
      xaxis: {
        categories: data.map(d => d.x),
        title: {
          text: 'Daily Return (%)'
        },
        labels: {
          rotate: -45,
          rotateAlways: true,
          style: {
            fontSize: '9px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Frequency'
        },
        labels: {
          formatter: (val) => Math.round(val)
        }
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const bin = histogramBins[dataPointIndex];
          if (!bin) return '';
          return `<div class="p-2 ${isDark ? 'bg-base-300' : 'bg-base-100'} rounded shadow">
            <b>Return Range</b><br/>
            ${bin.min.toFixed(2)}% to ${bin.max.toFixed(2)}%<br/>
            Count: ${bin.count}
          </div>`;
        }
      },
      annotations: {
        xaxis: [{
          x: mean.toFixed(2) + '%',
          borderColor: colors.error || '#d32f2f',
          label: {
            borderColor: colors.error || '#d32f2f',
            style: {
              color: '#fff',
              background: colors.error || '#d32f2f'
            },
            text: `μ=${mean.toFixed(2)}%, σ=${stdDev.toFixed(2)}%`
          }
        }]
      }
    };

    return {
      options: mergeThemeOptions(baseThemeOptions, componentOptions),
      series: [{
        name: 'Frequency',
        data: data.map(d => d.y)
      }],
      bins: histogramBins
    };
  }, [returnsSeries, height, baseThemeOptions, colors, isDark]);

  if (!options) {
    return (
      <div className="flex items-center justify-center text-base-content/60" style={{ height }}>
        No returns data available
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-bold mb-2">Returns Distribution</h4>
      <Chart
        options={options}
        series={series}
        type="bar"
        height={height}
      />
    </div>
  );
};

export default ReturnsHistogram;
